import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProfileMenu } from "../profiles/ProfileMenu";
import { ThemeToggle } from "../components/ThemeToggle";
import { ModeToggle } from "../components/ModeToggle";
import { FeedbackModal } from "../components/FeedbackModal";
import { HelpPanel } from "../components/HelpPanel";
import { Icon } from "./Icon";

/** App-shell top bar: global search + messages + theme + profile switcher. */
export function TopBar({ onMenu }: { onMenu: () => void }) {
  const [q, setQ] = useState("");
  const [feedback, setFeedback] = useState(false);
  const [help, setHelp] = useState(false);
  const navigate = useNavigate();

  function onSearch(e: FormEvent) {
    e.preventDefault();
    navigate(q.trim() ? `/jobs?q=${encodeURIComponent(q.trim())}` : "/jobs");
  }

  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-bg/85 px-4 py-2.5 backdrop-blur">
      <button type="button" onClick={onMenu} aria-label="Open menu" className="text-muted hover:text-ink lg:hidden">
        <Icon name="menu" className="h-5 w-5" />
      </button>

      <form className="relative flex max-w-xl flex-1 items-center" onSubmit={onSearch}>
        <Icon name="search" className="pointer-events-none absolute left-3 h-4 w-4 text-muted" />
        <input
          aria-label="Global search"
          className="w-full rounded-lg border border-line bg-surface-2 py-2 pl-9 pr-3 text-sm text-ink"
          placeholder="Search jobs, companies, routes…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </form>

      <div className="ml-auto flex items-center gap-2">
        <Link
          to="/messages"
          aria-label="Messages"
          className="grid h-9 w-9 place-items-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-ink"
        >
          <Icon name="messages" className="h-5 w-5" />
        </Link>
        <Link
          to="/notifications"
          aria-label="Notifications"
          className="grid h-9 w-9 place-items-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-ink"
        >
          <Icon name="bell" className="h-5 w-5" />
        </Link>
        <button
          type="button"
          onClick={() => setHelp(true)}
          aria-label="Help"
          title="Help & how-to guides"
          className="grid h-9 w-9 place-items-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-ink"
        >
          <Icon name="help" className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => setFeedback(true)}
          aria-label="Send feedback"
          title="Send feedback"
          className="grid h-9 w-9 place-items-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-ink"
        >
          <Icon name="idea" className="h-5 w-5" />
        </button>
        <ModeToggle />
        <ThemeToggle />
        <ProfileMenu />
      </div>
      {feedback && <FeedbackModal onClose={() => setFeedback(false)} />}
      {help && <HelpPanel onClose={() => setHelp(false)} />}
    </header>
  );
}
